import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { addToCart } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import Layout from '../components/Layout';
import { fetchProducts } from '../services/products';
import { SERVER_BASE_URL } from '../services/api';
import './Products.css';

const ITEMS_PER_PAGE = 12;

const Products = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http://') || image.startsWith('https://')) return image;
    return `${SERVER_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        status: 'active'
      };
      if (searchInput.trim()) params.search = searchInput.trim();
      const data = await fetchProducts(params);
      setProducts(data.data || []);
      setTotalPages(data.last_page ?? 1);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load products.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchInput]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const descriptionSnippet = (product) => {
    const text = product.subtitle || product.description || '';
    if (typeof text !== 'string') return '';
    const t = text.trim();
    return t.length <= 80 ? t : t.slice(0, 80) + '…';
  };

  return (
    <Layout>
      <div className="products-page">
        <div className="products-hero">
          <div className="products-hero-content">
            <h1>Our Products</h1>
            <p>High-Quality Agricultural Solutions</p>
            <form className="products-search-form" onSubmit={handleSearchSubmit}>
              <input
                type="search"
                placeholder="Search products..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="products-search-input"
              />
              <button type="submit" className="products-search-btn">Search</button>
            </form>
          </div>
        </div>

        <div className="products-content">
          <div className="products-container">
            {error && (
              <div className="products-list-error">{error}</div>
            )}

            {loading ? (
              <div className="loading">Loading products...</div>
            ) : products.length === 0 ? (
              <div className="products-empty">
                {searchInput.trim()
                  ? 'No products match your search.'
                  : 'No products available at the moment.'}
              </div>
            ) : (
              <>
                {total > 0 && (
                  <p className="products-count">
                    Showing page {currentPage} of {totalPages} ({total} product{total !== 1 ? 's' : ''})
                  </p>
                )}
                <div className="products-grid">
                  {products.map((product) => (
                    <div key={product.id} className="product-card">
                      <div
                        className="product-image"
                        onClick={() => handleProductClick(product.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {getImageUrl(product.image) ? (
                          <img src={getImageUrl(product.image)} alt={product.name} />
                        ) : (
                          <div className="product-image-placeholder">No image</div>
                        )}
                        {product.featured && (
                          <span className="product-featured-badge">Featured</span>
                        )}
                      </div>
                      <div onClick={() => handleProductClick(product.id)} style={{ cursor: 'pointer' }}>
                        <h3 className="product-title">{product.name}</h3>
                        <p className="product-description">{descriptionSnippet(product)}</p>
                        <p className="product-price">{formatNaira(toDisplayNaira(product.price, nairaPerYuan))}</p>
                      </div>
                      <div className="product-card-actions">
                        <button
                          type="button"
                          className="add-to-cart-btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(addToCart({ product, quantity: 1 }));
                            alert('Product added to cart!');
                          }}
                        >
                          Add to Cart
                        </button>
                        <button
                          type="button"
                          className="view-details-btn"
                          onClick={() => handleProductClick(product.id)}
                        >
                          View Details &gt;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-numbers">
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        let page;
                        if (totalPages <= 10) page = i + 1;
                        else if (currentPage <= 5) page = i + 1;
                        else if (currentPage >= totalPages - 4) page = totalPages - 9 + i;
                        else page = currentPage - 5 + i;
                        return (
                          <button
                            key={page}
                            type="button"
                            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Products;
